import { Box, Flex, Select, Text, Title, useMantineTheme } from "@mantine/core";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useCallback, useMemo, useState } from "react";
import { DocumentViews } from "src/components/DocumentGraph/DocumentViews";
import { selectOverviewFields } from "src/components/DocumentGraph/services/overviewFields";
import { useDatasources } from "src/hooks/useDatasources";
import { type DatasourceIngestionStatus, useIngestionStatus } from "src/hooks/useIngestionStatus";

export const DocumentsRoute = ({
    index,
    setSelectedIndex,
}: {
    index: string;
    setSelectedIndex: (value: string) => void;
}) => {
    const theme = useMantineTheme();
    const [filteredDocumentCount, setFilteredDocumentCount] = useState<number | undefined>(
        undefined,
    );
    const [datasourceSearch, setDatasourceSearch] = useState("");
    const [storedUserDatasources, _] = useLocalStorage<string[]>("userDatasources", []);
    const { data: chatVectorIndex = [] } = useDatasources();
    const getDisplayLabel = useCallback((label: string, datasourceLabel: string) => {
        const normalize = (s: string) => s.toLowerCase().trim();
        return normalize(label) !== normalize(datasourceLabel)
            ? `${datasourceLabel} (${label})`
            : label;
    }, []);

    const titles = useMemo(
        () =>
            Object.fromEntries(
                chatVectorIndex.map((o) => [o.id, getDisplayLabel(o.label, o.datasource_label)]),
            ) as Record<string, string>,
        [chatVectorIndex, getDisplayLabel],
    );

    const normalizeString = useCallback(
        (str: string | null | undefined) => (str ?? "").toLowerCase().replace(/[_\s]/g, ""),
        [],
    );

    const getAvailableIndicesInUserRoles = useCallback(() => {
        return chatVectorIndex.filter(
            (vectorIndex) =>
                vectorIndex.index_name !== "" &&
                (storedUserDatasources ?? []).some(
                    (userDs) =>
                        normalizeString(userDs).includes(
                            normalizeString(vectorIndex.datasource_label),
                        ) ||
                        normalizeString(vectorIndex.datasource_label).includes(
                            normalizeString(userDs),
                        ) ||
                        normalizeString(userDs).includes(normalizeString(vectorIndex.label)) ||
                        normalizeString(vectorIndex.label).includes(normalizeString(userDs)) ||
                        normalizeString(userDs) === normalizeString(vectorIndex.datasource_id),
                ),
        );
    }, [chatVectorIndex, storedUserDatasources, normalizeString]);

    const { data: ingestionStatus } = useIngestionStatus();

    const currentDatasource = chatVectorIndex.find((ds) => ds.id === index);
    const overviewFields = useMemo(
        () => selectOverviewFields(currentDatasource?.index_description ?? []),
        [currentDatasource],
    );
    const ingestionStatusForDatasource = Array.isArray(ingestionStatus?.data_sources)
        ? ingestionStatus?.data_sources.find(
              (value) => value.id === (currentDatasource?.datasource_id ?? index),
          )
        : undefined;

    const formatDocumentCount = (count: number): string => {
        return count.toLocaleString();
    };

    const ingestionStatusText = (
        status: DatasourceIngestionStatus | undefined,
    ): string | undefined => {
        if (!status) return undefined;

        const isFiltered = filteredDocumentCount !== undefined;

        if (isFiltered) {
            const count = filteredDocumentCount;
            return count > 0
                ? `Showing (${formatDocumentCount(count)}) ${count === 1 ? "document" : "documents"}`
                : "No documents found";
        }

        return status.total_count > 0
            ? `${status.ingestion_status} (${status.total_count} documents)`
            : `${status.ingestion_status}`;
    };

    return (
        <Flex px="xl" py="md" direction="column" gap="xs">
            <Flex justify="space-between">
                <Flex align={"baseline"} gap={"xs"} wrap={"wrap"}>
                    <Title lh={2} c="purple.8" order={2} style={{ textTransform: "uppercase" }}>
                        {titles[index]}
                    </Title>
                    {ingestionStatusForDatasource && (
                        <Text size="sm" fw={600} color="purple.7">
                            {ingestionStatusText(ingestionStatusForDatasource)}
                        </Text>
                    )}
                </Flex>
                <Box mt="xs" maw={300}>
                    <Select
                        id="data-sources"
                        value={index}
                        searchValue={datasourceSearch}
                        onSearchChange={setDatasourceSearch}
                        onDropdownOpen={() => setDatasourceSearch("")}
                        onChange={(value: string | null) => {
                            if (value) {
                                setSelectedIndex(value);
                            }
                        }}
                        data={getAvailableIndicesInUserRoles().map((item) => ({
                            label: getDisplayLabel(item.label, item.datasource_label),
                            value: item.id,
                        }))}
                        placeholder="Select a data source"
                        searchable
                        nothingFoundMessage="No data sources found"
                        styles={{
                            input: {
                                fontWeight: "bold",
                                color: theme.colors.purple[7],
                            },
                            dropdown: { maxHeight: 200, overflowY: "auto" },
                        }}
                        withScrollArea={false}
                        size="sm"
                        w={300}
                    />
                </Box>
            </Flex>
            <Box>
                <DocumentViews
                    key={index}
                    index={index}
                    rootLabel={titles[index] ?? index}
                    datasourceLabel={titles[index] ?? index}
                    fields={overviewFields}
                    onFilteredCountChange={setFilteredDocumentCount}
                />
            </Box>
        </Flex>
    );
};
